import React, { useState } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { getObjectSumValues } from 'utilitys/helpers/general';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';

import { useBattleCardsStore } from 'store/battlecards.store';

import { useMutationBulkTags } from 'api/mutations/bulkTags.mutation';
import { useMutationBulkTagsRemove } from 'api/mutations/bulkTagsRemove.mutation';
import { useMutationSaveFilterBulkAssignTags } from 'api/mutations/saveFilterBulkAssignTags.mutation';
import { useMutationSaveFilterBulkRemoveTags } from 'api/mutations/saveFilterBulkRemoveTags.mutation';
import { ICountersDataResponse } from 'api/queries/battleCards.query';

import SelectWithCreate from 'components/common/SelectWithCreate/SelectWithCreate';
import { useSelectWithCreateHook } from 'components/common/SelectWithCreate/SelectWithCreateHook';

import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/bulkAssign/bulkAssing.module.scss';
import { PinboardWidgetPills } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills';
import { executionConfig } from 'components/Dashboards/Project/ProjectWrapper/executionConfig';
import { Button } from 'components/DesignSystem/Button';
import { Modal } from 'components/DesignSystem/Modal';
import { Radio } from 'components/DesignSystem/Radio';
import { Select } from 'components/DesignSystem/Select';
import { Switch } from 'components/DesignSystem/Switch';
import { ConfirmSaveModal, CustomizedFormErrorMessage } from 'components/common';

interface IBulkAssignTagsProps {
    tagsValue: ISelectOption<string>[];
    radioOption: 'add' | 'remove';
    isApplyAutomatically: boolean;
}

interface Props {
    isShowModal: boolean;
    tagsOptions: ISelectOption<string>[];
    searchedFilterData: any;
    bulkAssigneeToTagsUniqIdNotification: number;
    bulkRemoveTagsUniqIdNotification: number;
    handleModalClose: () => void;
    setIsBulkLoading: React.Dispatch<React.SetStateAction<boolean>>;
    cardsCounters?: Record<keyof ICountersDataResponse, number>;
    pills: unknown[];
}

export const BulkAssignTags = ({
    isShowModal,
    handleModalClose,
    tagsOptions,
    searchedFilterData,
    bulkAssigneeToTagsUniqIdNotification,
    bulkRemoveTagsUniqIdNotification,
    setIsBulkLoading,
    cardsCounters,
    pills,
}: Props) => {
    const { handleSnackBar } = useCustomSnackBar();
    const [confirmModal, setConfirmModal] = useState<boolean>(false);
    const { mutate: addTagsMutation } = useMutationBulkTags();
    const { mutate: removeTagsMutation } = useMutationBulkTagsRemove();
    const { mutate: mutateSaveFilterBulkAssignTags } = useMutationSaveFilterBulkAssignTags();
    const { mutate: mutateSaveFilterBulkRemoveTags } = useMutationSaveFilterBulkRemoveTags();

    const { sendEventWithDimensions } = useEventWithDimensions();
    const {
        watch,
        handleSubmit,
        control,
        formState: { errors },
        reset,
        setValue,
    } = useForm<IBulkAssignTagsProps>({
        defaultValues: {
            tagsValue: [],
            radioOption: 'add',
            isApplyAutomatically: false,
        },
    });
    const { tagsValue, radioOption, isApplyAutomatically } = watch();

    const handleSave = () => {
        const tagsMutation: any = radioOption === 'add' ? addTagsMutation : removeTagsMutation;

        const fixedTagsValue = tagsValue.map((i) => ({ name: i.value }));

        const params =
            radioOption === 'add'
                ? {
                      body: {
                          bulkAssigneeToTagsUniqIdNotification,
                          tagToBulkAssignList: fixedTagsValue,
                          ...searchedFilterData,
                      },
                      querystring: {
                          lastRunActivities: getObjectSumValues(cardsCounters),
                      },
                  }
                : {
                      body: {
                          bulkRemoveTagsUniqIdNotification,
                          tagToRemoveList: fixedTagsValue,
                          ...searchedFilterData,
                      },
                      querystring: {
                          lastRunActivities: getObjectSumValues(cardsCounters),
                      },
                  };

        tagsMutation(params.body, {
            onSuccess: () => {
                setIsBulkLoading(true);
                sendEventWithDimensions({
                    category: 'Battlecards',
                    action: `Bulk Edit Tags - ${radioOption}`,
                    label: `filter: ${JSON.stringify(params.filter)}`,
                });

                if (isApplyAutomatically) {
                    radioOption === 'add'
                        ? mutateSaveFilterBulkAssignTags(params)
                        : mutateSaveFilterBulkRemoveTags(params);
                }
            },
            onError: (error) => {
                const message = 'Bulk request Failed';
                DEFAULT_ERROR({ error, message, handleError: handleSnackBar });
            },
            onSettled: () => {
                closeModal();
            },
        });
    };

    const closeModal = () => {
        handleModalClose();
        reset();
    };

    const { inputValue, onInputChange, onChange } = useSelectWithCreateHook({
        onChangeCallback: (value) =>
            setValue(
                'tagsValue',
                value.map((item) => ({ label: item.value.trim(), value: item.value.trim() })),
                { shouldDirty: true },
            ),
    });

    return (
        <>
            <Modal
                open={isShowModal}
                onClose={closeModal}
                title={<>{executionConfig.battlecards.title} - Bulk Tags</>}
                actions={
                    <>
                        <Button size={'large'} variant={'secondary'} onClick={closeModal}>
                            Cancel
                        </Button>

                        <Button
                            size={'large'}
                            variant={'primary'}
                            type={'submit'}
                            onClick={handleSubmit(() => setConfirmModal(true))}
                        >
                            {radioOption === 'add' ? 'Assign' : 'Remove'}
                        </Button>
                    </>
                }
            >
                <div className={classes.bulkAssingContent}>
                    <form onSubmit={handleSubmit(() => setConfirmModal(true))}>
                        {pills.length > 0 && (
                            <div className={classes.widgetFiltersWrapper}>
                                <div className={classes.widgetFiltersTitle}>Applied Filter:</div>
                                <div className={classes.widgetFiltersContent}>
                                    <PinboardWidgetPills key={'widgetFilters'} filters={pills} />
                                </div>
                            </div>
                        )}

                        <div className={classes.optionContainer}>
                            <Controller
                                render={({ field }) => (
                                    <Radio
                                        {...field}
                                        value={'add'}
                                        checked={radioOption === 'add'}
                                        label={'Add Tags'}
                                    />
                                )}
                                name={'radioOption'}
                                control={control}
                            />
                            <Controller
                                render={({ field }) => (
                                    <Radio
                                        {...field}
                                        value={'remove'}
                                        checked={radioOption === 'remove'}
                                        label={'Remove Tags'}
                                    />
                                )}
                                name={'radioOption'}
                                control={control}
                            />
                        </div>
                        <div className={classes.selectTitle}>
                            This will affect{' '}
                            <span className={classes.bold}>{getObjectSumValues(cardsCounters).toLocaleString()}</span>{' '}
                            activities matching the current filter
                        </div>
                        <div className={classes.selectWrapper}>
                            <Controller
                                render={({ field }) =>
                                    radioOption === 'add' ? (
                                        <SelectWithCreate
                                            {...field}
                                            options={tagsOptions}
                                            placeholder={'Select Tag...'}
                                            isMulti={true}
                                            isClearable={true}
                                            inputValue={inputValue}
                                            onInputChange={onInputChange}
                                            onChange={onChange}
                                        />
                                    ) : (
                                        <Select<ISelectOption<string>, true>
                                            {...field}
                                            options={tagsOptions}
                                            isMulti
                                            placeholder={'Select Tag...'}
                                        />
                                    )
                                }
                                name={'tagsValue'}
                                control={control}
                                rules={{
                                    validate: {
                                        required: (value) => value.length > 0 || 'Must select at least one tag',
                                    },
                                }}
                            />
                            {errors.tagsValue && <CustomizedFormErrorMessage text={errors.tagsValue.message} />}
                        </div>

                        {tagsValue.length > 0 && (
                            <div className={classes.messageForTheUser}>
                                You will receive a notification once the bulk update is completed.
                            </div>
                        )}

                        <div className={classes.applyAutomatically}>
                            <Switch
                                data-testid={'switch-advanced'}
                                onChange={(event) => setValue('isApplyAutomatically', event.target.checked)}
                                checked={isApplyAutomatically}
                                label={'Apply automatically to newly uploaded files'}
                                labelPlacement={'start'}
                            />
                        </div>
                    </form>
                </div>
            </Modal>

            <ConfirmSaveModal
                isShowModal={confirmModal}
                onHideModal={() => setConfirmModal(false)}
                onConfirm={() => {
                    setConfirmModal(false);
                    handleSave();
                }}
                actionButtonLabel={'Yes'}
                title={'Are you sure you want to run this bulk action?'}
            >
                Large number of assignments may take a while...
            </ConfirmSaveModal>
        </>
    );
};
